import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  InfoCircleOutlined,
  RightOutlined,
  SendOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { antdTokenFilter } from '@medcom/shared-ui/utils/tokenSearch.ts';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Col,
  ConfigProvider,
  DatePicker,
  type DatePickerProps,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  Popover,
  Radio,
  Row,
  Select,
  Steps,
  Typography,
  theme,
} from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import type { ErgebnisKonsultation } from '../../@types/native-api/generated/ksev20';
import { MainContent } from '../../components/MainContent';
import { showParamWarning } from '../../components/showParamWarning';
import type { PatientDataInput } from '../../graphql/generated/graphql.ts';
import { Gender, IdCardType, InsuranceCategory } from '../../graphql/generated/graphql.ts';
import { useAppStore } from '../../hooks/store/useAppStore';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { useEcardStore } from '../../hooks/store/useEcardStore.ts';
import { useCheckParameters } from '../../hooks/useCheckParameters';
import { API_DATE_FORMAT, SHORT_DATE_FORMAT } from '../../utils/dateFormatUtils';
import { insuranceCategoryFromCode, translateIdCardType, translateInsuranceCategory } from '../../utils/enumHelpers';
import { buildErrorDetails, getCardSlot } from '../../utils/ginoHelpers.ts';
import { serverSideEmailRule } from '../../utils/helpers.ts';
import { isValidSvnr } from '../../utils/svnrValidator';
import { hasFrontDeskRole } from '../../utils/user.ts';
import { PATIENT_BY_SVNR_SEARCH_QUERY, PATIENT_REGISTRATION_LOCK_QUERY, PATIENT_REGISTRATION_QUERY } from './PatientRegistration.gql';
import { EcardKonsultationModal } from './patient/EcardKonsultationModal.tsx';

const { useToken } = theme;
const envTag: string = window._env_.ENV_TAG;
const { Option } = Select;

type ExtendedPatientData = PatientDataInput & {
  coInsured: boolean;
};

const PATIENT_LOCK_HINT = {
  unsaved:
    'Patientendaten kommen aus dem AIS und können hier nicht geändert werden. Sind sie falsch, bitte im AIS korrigieren und die Anforderung erneut aus dem AIS öffnen.',
  // a saved Anforderung keeps its patient, a reopening from the AIS does not change it
  saved:
    'Patientendaten kommen aus dem AIS und können hier nicht geändert werden. Sind sie falsch, die Anforderung löschen, die Daten im AIS korrigieren und eine neue Anforderung aus dem AIS anlegen.',
};

// a saved Anforderung or its Nachforderung locks only once the AIS is known to own it; the server refuses a change either way
const usePatientLock = (): { locked: boolean; hint: string | null } => {
  const { requestIdForUpdate, requestIdForReorder, aisImportResult } = useAppStore(
    useShallow(state => ({
      requestIdForUpdate: state.requestIdForUpdate,
      requestIdForReorder: state.requestIdForReorder,
      aisImportResult: state.aisImportResult,
    }))
  );
  const savedRequestId = requestIdForUpdate || requestIdForReorder;
  const { data } = useQuery(PATIENT_REGISTRATION_LOCK_QUERY, {
    variables: { id: savedRequestId },
    skip: !savedRequestId,
  });
  if (savedRequestId) {
    return data?.request?.patientDataFromAis === true ? { locked: true, hint: PATIENT_LOCK_HINT.saved } : { locked: false, hint: null };
  }
  return aisImportResult?.patientDataFromAis ? { locked: true, hint: PATIENT_LOCK_HINT.unsaved } : { locked: false, hint: null };
};

const finishStepTitle = (requestIdForUpdate: string, requestIdForReorder: string) => {
  if (requestIdForUpdate) {
    return 'Bestehende Anforderung abschließen';
  }
  return requestIdForReorder ? 'Nachforderung abschließen' : 'Neue Anforderung abschließen';
};

const BirthdayPicker: React.FC<DatePickerProps> = props => (
  <DatePicker
    {...props}
    style={{ width: '100%' }}
    format={SHORT_DATE_FORMAT}
    placeholder="TT.MM.JJJJ"
    allowClear={false}
    disabledDate={current => current && (current.isAfter(dayjs()) || current.isBefore(dayjs().subtract(120, 'years')))}
  />
);

export const PatientRegistration: React.FC = () => {
  const {
    patientData,
    setPatientData,
    aisImportResult,
    setAisImportResult,
    requestIdForUpdate,
    requestIdForReorder,
    setParameterIdsWithUnknownBilling,
    paramQueue,
    setParamQueue,
  } = useAppStore();
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const { config, berechtigungen } = useEcardStore();

  const { token } = useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const invalidFields = location.state?.invalidFields ?? [];
  const validateInitial = location.state?.validateInitial ?? false;
  const [checkParameters] = useCheckParameters();
  const [form] = useForm<ExtendedPatientData>();
  const coInsuredWatch = useWatch('coInsured', form);
  const insuranceCodeWatch = useWatch('insuranceCode', form);
  const idCardTypeWatch = useWatch('idCardType', form);
  const { notification, message } = App.useApp();
  const [saveLoading, setSaveLoading] = useState(false);
  const auth = useAuth();
  const [konsultationOpen, setKonsultationOpen] = useState(false);
  const apolloClient = useApolloClient();

  const { data, loading } = useQuery(PATIENT_REGISTRATION_QUERY, {
    variables: {
      id: hasFrontDeskRole(auth.user) ? primaryDoctorId : currentDoctorId,
    },
  });

  const { locked: patientLocked, hint: lockHint } = usePatientLock();
  const idCardInputsDisabled = patientLocked || idCardTypeWatch === IdCardType.NICHT_VORHANDEN;

  const lockAlertStyle = css`
    margin-bottom: ${token.marginMD}px;
  `;

  const save = async (formValues: ExtendedPatientData) => {
    setSaveLoading(true);

    const patData: PatientDataInput = {
      externalId: patientData?.externalId?.trim() ?? '',
      // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
      birthday: (formValues.birthday as any) /* dayjs */
        .format(API_DATE_FORMAT),
      city: formValues.city.trim(),
      country: formValues.country.trim(),
      email: formValues.email.trim(),
      firstName: formValues.firstName.trim(),
      gender: formValues.gender,
      idCardAuthority: formValues.idCardAuthority.trim(),
      idCardNumber: formValues.idCardNumber.trim(),
      idCardType: formValues.idCardType,
      insuranceCategory: formValues.insuranceCategory,
      insuranceCode: formValues.insuranceCode ?? '', // insuranceCode cannot be undefined
      insuredPerson: formValues.insuredPerson
        ? {
            // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
            birthday: (formValues.insuredPerson.birthday as any) /* dayjs */
              .format(API_DATE_FORMAT),
            city: formValues.insuredPerson.city.trim(),
            country: formValues.insuredPerson.country.trim(),
            email: formValues.insuredPerson.email.trim(),
            firstName: formValues.insuredPerson.firstName.trim(),
            gender: formValues.insuredPerson.gender,
            lastName: formValues.insuredPerson.lastName.trim(),
            phone: formValues.insuredPerson.phone.trim(),
            street: formValues.insuredPerson.street.trim(),
            svnr: formValues.insuredPerson.svnr.trim(),
            title: formValues.insuredPerson.title.trim(),
            zip: formValues.insuredPerson.zip.trim(),
          }
        : null,
      lastName: formValues.lastName.trim(),
      phone: formValues.phone.trim(),
      street: formValues.street.trim(),
      svnr: formValues.svnr.trim(),
      title: formValues.title.trim(),
      zip: formValues.zip.trim(),
    };

    try {
      const result = await checkParameters({
        variables: {
          doctorId: currentDoctorId,
          patientData: patData,
          selectedParameters: paramQueue.selectedParameters.map(sp => ({
            parameterId: sp.id,
            parameterShortName: sp.shortName,
            parameterLongName: sp.longName,
            billingType: sp.billingInfo.billingType,
            specialRateId: sp.billingInfo.specialRateId,
            diagnoseId: sp.billingInfo.diagnoseId,
          })),
        },
      });

      if (!result.data?.checkParameters) {
        message.error('Fehler beim Speichern des Patienten');
        return;
      }

      setParamQueue(state => ({
        ...state.paramQueue,
        selectedParameters:
          result.data?.checkParameters?.selectedParameters.map(it => ({
            ...it.requestableParameter,
            billingInfo: it.billingInfo,
          })) ?? [],
      }));
      setParameterIdsWithUnknownBilling(result.data.checkParameters.unknownBillingParameters.map(it => it.id));

      if (result.data.checkParameters.unknownBillingParameters.length) {
        showParamWarning(
          notification,
          'Verrechnungssituation geändert',
          'Für folgende Parameter hat sich die Verrechnungssituation geändert:',
          result.data.checkParameters.unknownBillingParameters.map(it => (data?.doctor?.flipParamNames ? it.longName : it.shortName))
        );
      }
      if (result.data.checkParameters.unavailableParameters.length) {
        showParamWarning(
          notification,
          'Parameter nicht verfügbar',
          'Folgende Parameter stehen nicht zur Verfügung:',
          result.data.checkParameters.unavailableParameters
        );
      }

      setPatientData(patData);
      setAisImportResult(aisImportResult ? { ...aisImportResult, invalidPatientFields: [] } : null);
      navigate('/anforderung/parameterauswahl');
    } catch (_e) {
      message.error('Fehler beim Speichern des Patienten');
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      externalId: patientData?.externalId ?? '',
      title: patientData?.title ?? '',
      firstName: patientData?.firstName ?? '',
      lastName: patientData?.lastName ?? '',
      // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
      birthday: patientData?.birthday ? (dayjs(patientData.birthday) as any) : null,
      // biome-ignore lint/suspicious/noExplicitAny: null cast for form field
      gender: patientData?.gender ?? (null as any), // hack to force the user to choose a value
      svnr: patientData?.svnr ?? '',
      insuranceCode: patientData?.insuranceCode ?? undefined, // undefined shows placeholder correctly
      insuranceCategory: patientData?.insuranceCategory ?? InsuranceCategory.ERWERBSTAETIGE,
      city: patientData?.city ?? '',
      zip: patientData?.zip ?? '',
      street: patientData?.street ?? '',
      country: patientData?.country ?? '',
      phone: patientData?.phone ?? '',
      email: patientData?.email ?? '',
      idCardType: patientData?.idCardType ?? IdCardType.NICHT_VORHANDEN,
      idCardNumber: patientData?.idCardNumber ?? '',
      idCardAuthority: patientData?.idCardAuthority ?? '',
      coInsured: !!patientData?.insuredPerson,
      insuredPerson: patientData?.insuredPerson
        ? {
            ...patientData.insuredPerson,
            // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
            birthday: patientData.insuredPerson.birthday ? (dayjs(patientData.insuredPerson.birthday) as any) : null,
          }
        : null,
    });

    if (patientData && validateInitial) {
      form.validateFields();
    }
  }, [form, patientData, validateInitial]);

  const [testPatientInsured, setTestPatientInsured] = useState(true);
  const [testPatientWithAddress, setTestPatientWithAddress] = useState(true);
  const [testPatientCoInsured, setTestPatientCoInsured] = useState(false);

  const fillTestPatient = () => {
    form.setFieldsValue({
      externalId: '',
      title: 'Mag.',
      firstName: 'Max Tester',
      lastName: 'Mustermann',
      // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
      birthday: dayjs('1998-08-10') as any,
      gender: Gender.MALE,
      svnr: testPatientInsured ? '1111111111' : '',
      insuranceCode: testPatientInsured && data?.doctor?.insurances.length ? data?.doctor.insurances[0].code : undefined,
      insuranceCategory: InsuranceCategory.ERWERBSTAETIGE,
      city: testPatientWithAddress ? 'Wien' : '',
      zip: testPatientWithAddress ? '1010' : '',
      street: testPatientWithAddress ? 'Wollzeile 11/2. Stock' : '',
      country: '',
      phone: '0664 1234567',
      email: 'patient@example.org',
      idCardType: IdCardType.FUEHERSCHEIN,
      idCardNumber: '12345678',
      idCardAuthority: 'BH Wien',
      coInsured: testPatientCoInsured,
      insuredPerson: testPatientCoInsured
        ? {
            title: 'Dr.',
            firstName: 'John',
            lastName: 'Doe',
            // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
            birthday: dayjs('1985-03-11') as any,
            gender: null,
            svnr: '1181010180',
            city: 'St. Pölten',
            zip: '3100',
            street: 'Hauptstraße 1',
            country: '',
            phone: '0676 7654321',
            email: 'insured@example.org',
          }
        : null,
    });
  };

  const fillEcardData = async () => {
    if (!config) {
      notification.error({ title: 'Konfiguration nicht gefunden' });
      return;
    }

    try {
      const response = await window.nativeApi!.ginoStatus(config.cardReaderAddress, {
        returnEhicData: true,
      });

      if (response.error) {
        notification.error({ title: 'Fehler beim Lesen der e-card Daten', description: buildErrorDetails(response) });
        return;
      }

      const slot = getCardSlot(response, 'e-card');
      const cardData = slot?.cardData;
      if (!cardData) {
        notification.error({
          title: 'Fehler beim Lesen der e-card Daten',
          description: 'Es konnte keine Daten extrahiert werden. Ist eine e-card gesteckt?',
        });
        return;
      }

      let patientData;
      if (cardData.nummer) {
        const result = await apolloClient.query({
          query: PATIENT_BY_SVNR_SEARCH_QUERY,
          variables: {
            svnr: cardData.nummer,
          },
        });
        if (result.data?.newestRequestBySvnr) {
          patientData = result.data.newestRequestBySvnr.patientData;
        }
      }

      form.setFieldsValue({
        title: `${cardData.titel ?? ''} ${cardData.titelHinten ?? ''}`.trim(),
        firstName: cardData.vorname ?? '',
        lastName: cardData.nachname ?? '',
        // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
        birthday: cardData.geburtsdatum ? (dayjs(cardData.geburtsdatum, 'DD.MM.YYYY') as any) : null,
        gender:
          cardData.geschlecht === 'M'
            ? Gender.MALE
            : cardData.geschlecht === 'F' || cardData.geschlecht === 'W'
              ? Gender.FEMALE
              : patientData?.gender
                ? patientData.gender
                : // biome-ignore lint/suspicious/noExplicitAny: null cast for form field
                  (null as any),
        svnr: cardData.nummer ?? '',
        insuranceCode: cardData.ekvk?.traegerNummer?.substring(0, 2) ?? undefined,
        city: patientData?.city ?? '',
        zip: patientData?.zip ?? '',
        street: patientData?.street ?? '',
        country: patientData?.country ?? '',
        phone: patientData?.phone ?? '',
        email: patientData?.email ?? '',
        idCardType: patientData?.idCardType ?? IdCardType.NICHT_VORHANDEN,
        idCardNumber: patientData?.idCardNumber ?? '',
        idCardAuthority: patientData?.idCardAuthority ?? '',
      });
    } catch (_e) {
      notification.error({
        title: 'Es ist ein unbekannter Fehler bei der Verbindung zum Kartenlesegerät aufgetreten',
      });
    }
  };

  const fillKonsultation = async (data: ErgebnisKonsultation) => {
    setKonsultationOpen(false);
    const patient = data.konsultationsDaten?.versichertenDaten;
    const insuree = data.konsultationsDaten?.angehoerigenDaten;

    console.log(data);

    let patientData;
    if (patient?.svNummer) {
      const result = await apolloClient.query({
        query: PATIENT_BY_SVNR_SEARCH_QUERY,
        variables: {
          svnr: patient.svNummer,
        },
      });
      if (result.data?.newestRequestBySvnr) {
        patientData = result.data.newestRequestBySvnr.patientData;
      }
    }

    form.setFieldsValue({
      title: `${patient?.titelVorne ?? ''} ${patient?.titelHinten ?? ''}`.trim(),
      firstName: patient?.vorname ?? '',
      lastName: patient?.nachname ?? '',
      // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
      birthday: patient?.geburtsdatum ? (dayjs(patient?.geburtsdatum, 'DD.MM.YYYY') as any) : null,
      gender:
        patient?.geschlecht === 'M'
          ? Gender.MALE
          : patient?.geschlecht === 'F' || patient?.geschlecht === 'W'
            ? Gender.FEMALE
            : patientData?.gender
              ? patientData.gender
              : // biome-ignore lint/suspicious/noExplicitAny: null cast for form field
                (null as any),
      svnr: patient?.svNummer ?? '',
      insuranceCode: data.konsultationsDaten?.konsultationsBeleg?.leistungsSvtCode ?? undefined,
      insuranceCategory:
        insuranceCategoryFromCode(data.konsultationsDaten?.konsultationsBeleg?.versichertenKategorie) ?? InsuranceCategory.ERWERBSTAETIGE,
      city: patientData?.city ?? '',
      zip: patientData?.zip ?? '',
      street: patientData?.street ?? '',
      country: patientData?.country ?? '',
      phone: patientData?.phone ?? '',
      email: patientData?.email ?? '',
      idCardType: patientData?.idCardType ?? IdCardType.NICHT_VORHANDEN,
      idCardNumber: patientData?.idCardNumber ?? '',
      idCardAuthority: patientData?.idCardAuthority ?? '',
      coInsured: !!insuree,
      insuredPerson: insuree
        ? {
            title: `${insuree?.titelVorne ?? ''} ${insuree?.titelHinten ?? ''}`.trim(),
            firstName: insuree?.vorname ?? '',
            lastName: insuree?.nachname ?? '',
            // biome-ignore lint/suspicious/noExplicitAny: dayjs cast for Ant Design form
            birthday: insuree?.geburtsdatum ? (dayjs(insuree?.geburtsdatum, 'DD.MM.YYYY') as any) : null,
            gender:
              insuree?.geschlecht === 'M'
                ? Gender.MALE
                : insuree?.geschlecht === 'F' || insuree?.geschlecht === 'W'
                  ? Gender.FEMALE
                  : patientData?.insuredPerson?.gender
                    ? patientData?.insuredPerson?.gender
                    : // biome-ignore lint/suspicious/noExplicitAny: null cast for form field
                      (null as any),
            svnr: insuree?.svNummer ?? '',
            city: patientData?.insuredPerson?.city ?? '',
            zip: patientData?.insuredPerson?.zip ?? '',
            street: patientData?.insuredPerson?.street ?? '',
            country: patientData?.insuredPerson?.country ?? '',
            phone: patientData?.insuredPerson?.phone ?? '',
            email: patientData?.insuredPerson?.email ?? '',
          }
        : null,
    });
  };

  return (
    <MainContent size="medium">
      <Steps
        items={[
          {
            status: 'process',
            title: 'Patient',
            icon: <UserOutlined />,
          },
          {
            status: 'wait',
            title: 'Parameterauswahl',
            icon: <TableOutlined />,
          },
          {
            status: 'wait',
            title: finishStepTitle(requestIdForUpdate, requestIdForReorder),
            icon: <SendOutlined />,
          },
        ]}
      />
      <PageHeader
        title={
          <span>
            Patient erfassen{' '}
            <Popover
              trigger={['click']}
              placement="right"
              title="Info Verrechnung"
              content={
                <div style={{ maxWidth: '500px' }}>
                  <h4>Privat an Patient</h4>
                  <p>Eine Adressangabe (Straße, PLZ und Ort) ist erforderlich, um Privat an den Patienten anfordern zu können.</p>
                  <h4>Kassenanforderung</h4>
                  <p>
                    Eine gültige SVNR und die Auswahl einer Gesundheitskasse ist erforderlich, um Kassenleistungen in Anspruch nehmen zu
                    können.
                  </p>
                  <h4>Hauptversicherte Person</h4>
                  <p>Wenn der Patient mitversichert ist, dann gelten oben genannte Kriterien auch für die hauptversicherte Person.</p>
                </div>
              }
            >
              <InfoCircleOutlined style={{ cursor: 'pointer' }} />
            </Popover>
          </span>
        }
        style={{ padding: 0, paddingBottom: token.paddingMD, paddingTop: token.paddingMD }}
        extra={
          !patientLocked && (
            <Flex vertical align="end" gap="small">
              {window.nativeApi && config && (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'chip',
                        label: 'Patientendaten auf e-card Chip',
                        icon: <UserOutlined />,
                        onClick: () => fillEcardData(),
                      },
                      ...(berechtigungen.includes('KSE.FULL')
                        ? [
                            {
                              key: 'kse',
                              label: 'Konsultation / Behandlungsfall',
                              icon: <UserOutlined />,
                              onClick: () => setKonsultationOpen(true),
                            },
                          ]
                        : []),
                      /* VDAS Versichertendatenabfrage and SAS Adressabfrage currently not implemented / not in use / no permissions */
                    ],
                  }}
                  placement="bottomLeft"
                >
                  <Button>
                    e-card Datenabfrage <DownOutlined />
                  </Button>
                </Dropdown>
              )}
              {envTag && envTag !== 'prod' && (
                <div>
                  <Checkbox checked={testPatientInsured} onChange={e => setTestPatientInsured(e.target.checked)}>
                    Versichert?
                  </Checkbox>
                  <Checkbox checked={testPatientWithAddress} onChange={e => setTestPatientWithAddress(e.target.checked)}>
                    Mit Adresse?
                  </Checkbox>
                  <Checkbox checked={testPatientCoInsured} onChange={e => setTestPatientCoInsured(e.target.checked)}>
                    Mitversichert?
                  </Checkbox>
                  <Button onClick={fillTestPatient} icon={<UserOutlined />}>
                    Testpatient ausfüllen
                  </Button>
                </div>
              )}
            </Flex>
          )
        }
      />
      {!!invalidFields.length && (
        <>
          <Alert
            type="warning"
            showIcon
            title="Bitte ergänzen Sie die Angaben zum Patient"
            closable
            description={`Folgende Felder konnten nicht korrekt vom AIS übernommen werden: ${invalidFields.join(', ')}`}
          />
          <p />
        </>
      )}
      {lockHint && <Alert type="info" showIcon className={lockAlertStyle} title={lockHint} />}
      <Form<ExtendedPatientData> labelCol={{ span: 6 }} colon={false} labelAlign="left" form={form} onFinish={save}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <ConfigProvider componentDisabled={patientLocked}>
              <Form.Item name="title" label="Titel">
                <Input />
              </Form.Item>
              <Form.Item name="firstName" label="Vorname" rules={[{ required: true, whitespace: true }]}>
                <Input autoFocus />
              </Form.Item>
              <Form.Item name="lastName" label="Nachname" rules={[{ required: true, whitespace: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Geburtstag" name="birthday" rules={[{ type: 'date', required: true }]}>
                <BirthdayPicker />
              </Form.Item>
              <Form.Item
                name="gender"
                label="Geschlecht"
                rules={[
                  {
                    type: 'enum',
                    required: true,
                    enum: [Gender.FEMALE, Gender.MALE],
                    message: 'Geben Sie das Geschlecht an',
                  },
                ]}
              >
                <Radio.Group
                  options={[
                    { label: 'weiblich', value: Gender.FEMALE },
                    { label: 'männlich', value: Gender.MALE },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="svnr"
                label="SVNR"
                rules={[
                  {
                    validator: (_, value) =>
                      !value
                        ? Promise.resolve()
                        : isValidSvnr(value)
                          ? Promise.resolve()
                          : Promise.reject('Die eingegebene SVNR ist ungültig'),
                  },
                  { required: !!insuranceCodeWatch },
                ]}
              >
                <Input placeholder="10-stellig" maxLength={10} />
              </Form.Item>
              <Form.Item
                name="insuranceCode"
                label="Kasse"
                tooltip={
                  <>
                    <CheckOutlined style={{ color: token['green-7'] }} /> anforderbar
                    <br />
                    <CloseOutlined style={{ color: token.colorError }} /> nicht anforderbar
                  </>
                }
              >
                <Select
                  popupMatchSelectWidth={false}
                  showSearch={{ filterOption: antdTokenFilter }}
                  allowClear
                  placeholder="Privat"
                  optionLabelProp="taglabel"
                  loading={loading}
                >
                  {[...(data?.insurances ?? [])]
                    .sort((a, b) => a.shortName.localeCompare(b.shortName))
                    .map(insurance => {
                      const indicator = data?.doctor?.insurances?.find(di => di.code === insurance.code) ? (
                        <CheckOutlined style={{ color: token['green-7'] }} />
                      ) : (
                        <CloseOutlined style={{ color: token.colorError }} />
                      );
                      return (
                        <Option
                          key={insurance.code}
                          value={insurance.code}
                          taglabel={
                            <span>
                              {indicator} {insurance.shortName}
                            </span>
                          }
                          search={`${insurance.shortName} ${insurance.name}`}
                        >
                          <div>
                            {indicator} {insurance.shortName}
                          </div>
                          <Typography.Text type="secondary">{insurance.name}</Typography.Text>
                        </Option>
                      );
                    })}
                </Select>
              </Form.Item>
              <Form.Item
                name="insuranceCategory"
                label="Kategorie"
                wrapperCol={{ span: 18 }}
                rules={[
                  {
                    type: 'enum',
                    enum: Object.keys(InsuranceCategory),
                  },
                ]}
              >
                <Select popupMatchSelectWidth={false} showSearch={{ filterOption: antdTokenFilter }}>
                  {Object.keys(InsuranceCategory).map(category => (
                    <Option key={category} value={category}>
                      {/* leading code digits align across the option list */}
                      <span className={tabularNums}>{translateInsuranceCategory(category)}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </ConfigProvider>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <ConfigProvider componentDisabled={patientLocked}>
              <Form.Item name="street" label="Straße u. Nr.">
                <Input />
              </Form.Item>
              <Form.Item name="city" label="Ort">
                <Input />
              </Form.Item>
              <Row>
                <Col span={10}>
                  <Form.Item labelCol={{ span: 14 }} name="zip" label="PLZ">
                    <Input style={{ marginLeft: '4px' }} />
                  </Form.Item>
                </Col>
                <Col span={14}>
                  <Form.Item labelCol={{ span: 5 }} name="country" label={<span style={{ paddingLeft: token.paddingSM }}>Land</span>}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="phone" label="Telefon">
                <Input />
              </Form.Item>
              <Form.Item name="email" label="E-Mail" rules={[serverSideEmailRule]} validateDebounce={200}>
                <Input />
              </Form.Item>
              <Form.Item
                name="idCardType"
                label="Ausweis"
                wrapperCol={{ span: 18 }}
                rules={[
                  {
                    type: 'enum',
                    enum: Object.keys(IdCardType),
                  },
                ]}
              >
                <Select
                  popupMatchSelectWidth={false}
                  showSearch={{ filterOption: antdTokenFilter }}
                  onChange={value => {
                    if (value === IdCardType.NICHT_VORHANDEN) {
                      form.setFieldValue('idCardNumber', '');
                      form.setFieldValue('idCardAuthority', '');
                    }
                  }}
                >
                  {Object.keys(IdCardType).map(type => (
                    <Option key={type} value={type}>
                      {translateIdCardType(type)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="idCardNumber" label="Ausweisnummer">
                <Input disabled={idCardInputsDisabled} />
              </Form.Item>
              <Form.Item name="idCardAuthority" label="Behörde">
                <Input disabled={idCardInputsDisabled} />
              </Form.Item>
            </ConfigProvider>
          </Col>
        </Row>
        <Form.Item noStyle name="coInsured" valuePropName="checked">
          <Checkbox
            disabled={patientLocked}
            onChange={e => {
              form.setFieldValue(
                'insuredPerson',
                e.target.checked
                  ? {
                      title: '',
                      firstName: '',
                      lastName: '',
                      birthday: '',
                      gender: null,
                      svnr: '',
                      city: '',
                      zip: '',
                      street: '',
                      country: '',
                      phone: '',
                      email: '',
                    }
                  : null
              );
            }}
          >
            <h4 style={{ margin: 0 }}>Mitversichert?</h4>
          </Checkbox>
        </Form.Item>
        {coInsuredWatch && (
          <ConfigProvider componentDisabled={patientLocked}>
            <Divider titlePlacement="left">Hauptversicherte Person bzw. Rechnungsempfänger</Divider>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                <Form.Item name={['insuredPerson', 'title']} label="Titel">
                  <Input />
                </Form.Item>
                <Form.Item name={['insuredPerson', 'firstName']} label="Vorname" rules={[{ required: true, whitespace: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name={['insuredPerson', 'lastName']} label="Nachname" rules={[{ required: true, whitespace: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item label="Geburtstag" name={['insuredPerson', 'birthday']} rules={[{ type: 'date', required: true }]}>
                  <BirthdayPicker />
                </Form.Item>
                <Form.Item
                  name={['insuredPerson', 'gender']}
                  label="Geschlecht"
                  rules={[
                    {
                      type: 'enum',
                      required: false,
                      enum: [Gender.FEMALE, Gender.MALE],
                      message: 'Geben Sie das Geschlecht an',
                    },
                  ]}
                >
                  <Radio.Group
                    options={[
                      { label: 'weiblich', value: Gender.FEMALE },
                      { label: 'männlich', value: Gender.MALE },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name={['insuredPerson', 'svnr']}
                  label="SVNR"
                  rules={[
                    {
                      required: true,
                      validator: (_, value) =>
                        isValidSvnr(value) ? Promise.resolve() : Promise.reject('Die eingegebene SVNR ist ungültig'),
                    },
                  ]}
                >
                  <Input placeholder="10-stellig" maxLength={10} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                <Form.Item name={['insuredPerson', 'street']} label="Straße u. Nr.">
                  <Input />
                </Form.Item>
                <Form.Item name={['insuredPerson', 'zip']} label="PLZ">
                  <Input />
                </Form.Item>
                <Form.Item name={['insuredPerson', 'city']} label="Ort">
                  <Input />
                </Form.Item>
                <Form.Item name={['insuredPerson', 'country']} label="Land">
                  <Input />
                </Form.Item>
                <Form.Item name={['insuredPerson', 'phone']} label="Telefon">
                  <Input />
                </Form.Item>
                <Form.Item name={['insuredPerson', 'email']} label="E-Mail" rules={[serverSideEmailRule]} validateDebounce={200}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </ConfigProvider>
        )}
        <Divider />
        <Button htmlType="submit" type="primary" icon={<RightOutlined />} loading={saveLoading}>
          Weiter zur Parameterauswahl
        </Button>
      </Form>
      <EcardKonsultationModal
        open={konsultationOpen}
        onClose={() => setKonsultationOpen(false)}
        onKonsultationCompleted={fillKonsultation}
      />
    </MainContent>
  );
};
